import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import moment from 'moment';
import 'moment/locale/it';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, 
    faChevronLeft, 
    faChevronRight, 
    faBriefcase, 
    faCalendarDays, 
    faUserGroup, 
    faCar, 
    faToolbox, 
    faNoteSticky, 
    faPlus, 
    faPencilAlt, 
    faTrashAlt,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { useColorPalette } from '../hooks/useColorPalette';
import WizardHeader from '../components/WizardHeader';
import JobsCalendar from './JobsCalendar';

// Funzione per calcolare la data della Pasqua
function getEasterDate(year) {
  const f = Math.floor;
  const G = year % 19;
  const C = f(year / 100);
  const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
  const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
  const J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7;
  const L = I - J;
  const month = 3 + f((L + 40) / 44);
  const day = L + 28 - 31 * f(month / 4);
  return new Date(year, month - 1, day);
}

// Funzione per calcolare tutte le festività italiane
function getItalianHolidays(year) {
  const pasqua = getEasterDate(year);
  const pasquetta = new Date(pasqua);
  pasquetta.setDate(pasqua.getDate() + 1);

  const fixedHolidays = [
    `${year}-01-01`, // Capodanno
    `${year}-01-06`, // Epifania
    `${year}-04-25`, // Liberazione
    `${year}-05-01`, // Lavoro
    `${year}-06-02`, // Repubblica
    `${year}-08-15`, // Ferragosto
    `${year}-11-01`, // Ognissanti
    `${year}-12-08`, // Immacolata
    `${year}-12-25`, // Natale
    `${year}-12-26`, // Santo Stefano
  ];
  
  // Formattare le date di Pasqua e Pasquetta in formato ISO
  const pasquaMonth = (pasqua.getMonth() + 1).toString().padStart(2, '0');
  const pasquaDay = pasqua.getDate().toString().padStart(2, '0');
  const pasquettaMonth = (pasquetta.getMonth() + 1).toString().padStart(2, '0');
  const pasquettaDay = pasquetta.getDate().toString().padStart(2, '0');
  
  return [
    ...fixedHolidays,
    `${year}-${pasquaMonth}-${pasquaDay}`, // Pasqua
    `${year}-${pasquettaMonth}-${pasquettaDay}`, // Pasquetta
  ];
}

// Importo gli stili per il wizard
import '../assets/styles/wizard.css';

// Il componente StepIcon è stato spostato nel file WizardHeader.jsx

// Importo i componenti di selezione ufficiali
import JobSelect from '../components/JobSelect';
import EmployeeSelect from '../components/EmployeeSelect';
import VehicleSelect from '../components/VehicleSelect';
import EquipmentSelect from '../components/EquipmentSelect';

// Funzione per generare un ID univoco (alternativa a uuid)
const generateUniqueId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

const JobScheduling = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const accountData = useSelector((state) => state.account.data);
    const colors = useColorPalette(); // Utilizzo dell'hook per ottenere i colori del tema
    const token = useSelector((state) => state.auth.token);
    
    // Stato per controllare la visibilità del modale calendario
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    
    // Definizione degli step del wizard
    const steps = [
        { title: 'Commessa', icon: faBriefcase },
        { title: 'Schedulazione', icon: faCalendarDays },
        { title: 'Risorse', icon: faUserGroup },
        { title: 'Note', icon: faNoteSticky }
    ];

    // Funzione per ottenere il colore dello stato della commessa dalle impostazioni dell'account
    const getJobStatusColor = (statusId) => {
        if (!statusId || !accountData || !accountData.settings || !accountData.settings.jobStatus) return '#6c757d'; // Default: grigio
        
        const matchingStatus = accountData.settings.jobStatus.find(status => status._id === statusId);
        
        if (matchingStatus && matchingStatus.color) {
            return matchingStatus.color;
        }
        
        return '#6c757d'; // Default: grigio
    };
    
    // Funzione per ottenere il colore dello stato della richiesta di assistenza dalle impostazioni dell'account
    const getAssistanceStatusColor = (statusId) => {
        if (!statusId || !accountData || !accountData.settings || !accountData.settings.assistanceRequestsStatus) return '#6c757d'; // Default: grigio
        
        const matchingStatus = accountData.settings.assistanceRequestsStatus.find(status => status._id === statusId);
        
        if (matchingStatus && matchingStatus.color) {
            return matchingStatus.color;
        }
        
        return '#6c757d'; // Default: grigio
    };
    
    // Funzione per ottenere il colore del tipo di commessa dalle impostazioni dell'account
    const getJobTypeColor = (jobTypeId) => {
        if (!jobTypeId || !accountData || !accountData.settings || !accountData.settings.jobTypes) return '#6c757d'; // Default: grigio
        
        const matchingType = accountData.settings.jobTypes.find(type => type._id === jobTypeId);
        
        if (matchingType && matchingType.color) {
            return matchingType.color;
        }
        
        return '#6c757d'; // Default: grigio
    };
    
    // Funzione per ottenere il colore dello stato della manutenzione dalle impostazioni dell'account
    const getMaintenanceStatusColor = (statusId) => {
        if (!statusId || !accountData || !accountData.settings || !accountData.settings.maintenanceStatus) return '#6c757d'; // Default: grigio
        
        const matchingStatus = accountData.settings.maintenanceStatus.find(status => status._id === statusId);
        
        if (matchingStatus && matchingStatus.color) {
            return matchingStatus.color;
        }
        
        return '#6c757d'; // Default: grigio
    };
    
    // Funzione per ottenere il colore dello stato delle attività di manutenzione dalle impostazioni dell'account
    const getMaintenanceActivityStatusColor = (statusId) => {
        if (!statusId || !accountData || !accountData.settings || !accountData.settings.maintenanceActivitiesStatus) return '#6c757d'; // Default: grigio
        
        const matchingStatus = accountData.settings.maintenanceActivitiesStatus.find(status => status._id === statusId);
        
        if (matchingStatus && matchingStatus.color) {
            return matchingStatus.color;
        }
        
        return '#6c757d'; // Default: grigio
    };
    
    // Funzione per formattare la data in formato ISO (YYYY-MM-DD)
    const formatDateToISO = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Funzione per generare un array di date tra due date (incluse), solo giorni lavorativi
    const getDatesBetween = (startDate, endDate) => {
        // Utilizziamo direttamente le stringhe ISO passate come parametri
        // senza convertirle in oggetti Date per evitare problemi di fuso orario
        
        // Ottieni gli anni compresi tra le date di inizio e fine
        const startYear = parseInt(startDate.substring(0, 4));
        const endYear = parseInt(endDate.substring(0, 4));
        
        // Genera la lista di tutte le festività italiane per ogni anno coinvolto
        let italianHolidays = [];
        for (let year = startYear; year <= endYear; year++) {
            italianHolidays = [...italianHolidays, ...getItalianHolidays(year)];
        }
        
        console.log('Festività italiane nel periodo:', italianHolidays);
        
        // Caso speciale: se le date sono uguali, consentire la pianificazione anche nei festivi
        // Verificare solo che non sia weekend (sabato o domenica)
        if (startDate === endDate) {
            const day = moment(startDate).day();
            // Se è sabato (6) o domenica (0), non includiamo la data
            if (day === 0 || day === 6) {
                console.log('Data singola è nel weekend, restituisco array vuoto');
                return [];
            }
            
            // Se è una festività, avvisiamo ma includiamo comunque la data
            if (italianHolidays.includes(startDate)) {
                console.log('ATTENZIONE: Data singola è una festività, ma la pianificazione è permessa');
            }
            
            return [startDate];
        }
        
        const dateArray = [];
        
        // Usiamo moment.js per gestire correttamente le date
        let currentMoment = moment(startDate);
        const lastMoment = moment(endDate);
        
        // Aggiungiamo ogni data all'array, escludendo i weekend e le festività
        while (currentMoment.isSameOrBefore(lastMoment, 'day')) {
            const currentDate = currentMoment.format('YYYY-MM-DD');
            const day = currentMoment.day();
            
            // Includiamo solo i giorni dal lunedì (1) al venerdì (5) che non sono festività
            if (day > 0 && day < 6 && !italianHolidays.includes(currentDate)) {
                // Formato ISO YYYY-MM-DD senza componente oraria
                dateArray.push(currentDate);
            }
            currentMoment.add(1, 'days');
        }
        
        console.log('Date lavorative generate (esclusi weekend e festività):', dateArray);
        return dateArray;
    };

    const [currentStep, setCurrentStep] = useState(1);
    
    // Stato per la commessa selezionata
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobLoading, setJobLoading] = useState(false);
    
    // Stato per date e orari
    const [startDate, setStartDate] = useState(formatDateToISO(new Date()));
    const [endDate, setEndDate] = useState(formatDateToISO(new Date()));
    const [startTime, setStartTime] = useState('08:00');
    const [endTime, setEndTime] = useState('17:00');
    
    // Stato per risorse
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [selectedEquipments, setSelectedEquipments] = useState([]);
    
    // Stato per note
    const [planningNotes, setPlanningNotes] = useState('');
    
    // Stato per pianificazioni create
    const [plannings, setPlannings] = useState([]);
    
    // Stato per la modalità di modifica
    const [editMode, setEditMode] = useState(false);
    const [editPlanningId, setEditPlanningId] = useState(null);
    
    // Recupero il parametro jobId dall'URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const jobId = params.get('jobId');
        
        if (jobId) {
            setJobLoading(true);
            // Recupera i dati della commessa dal backend
            axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/jobs`, {
                headers: { 'auth-token': token },
                params: { jobId: jobId }
            })
            .then(response => {
                if (response.data && response.data._id) {
                    // Crea l'oggetto con la struttura richiesta da JobSelect
                    const jobData = response.data;
                    const jobOption = {
                        value: jobData._id,
                        label: jobData.code + ' - ' + (jobData.customerLabel || 'Cliente N/D'),
                        data: jobData
                    };
                    
                    // Imposta la commessa selezionata
                    setSelectedJob(jobOption);
                    
                    // Se necessario, forza lo step 1 per essere sicuri che l'utente veda la commessa preselezionata
                    setCurrentStep(1);
                }
            })
            .catch(error => {
                console.error('Errore nel recuperare i dettagli della commessa:', error);
                toast.error('Impossibile caricare i dettagli della commessa richiesta');
            })
            .finally(() => {
                setJobLoading(false);
            });
        }
    }, [location.search, token]);
    
    // Stato per le modali di conferma
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [planningToDelete, setPlanningToDelete] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Funzione per passare allo step successivo
    const goToNextStep = () => {
        // Validazione prima di procedere
        if (currentStep === 1 && !selectedJob) {
            toast.warning('Seleziona una commessa prima di procedere');
            return;
        } else if (currentStep === 2) {
            if (!startDate || !endDate) {
                toast.warning('Seleziona data di inizio e fine');
                return;
            }
            if (!startTime || !endTime) {
                toast.warning('Seleziona orario di inizio e fine');
                return;
            }
            if (startDate > endDate) {
                toast.warning('La data di inizio deve essere precedente o uguale alla data di fine');
                return;
            }
            if (startDate === endDate && startTime > endTime) {
                toast.warning('L\'orario di inizio deve essere precedente all\'orario di fine');
                return;
            }
        }

        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    // Funzione per tornare allo step precedente
    const goToPreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Funzione per aggiungere o aggiornare una pianificazione
    const addOrUpdatePlanning = () => {
        if (!selectedJob) {
            toast.error('Seleziona una commessa per aggiungere la pianificazione');
            setCurrentStep(1);
            return;
        }
        
        if (!startDate || !endDate || !startTime || !endTime) {
            toast.error('Completa tutte le informazioni di schedulazione');
            setCurrentStep(2);
            return;
        }
        
        // Genera tutte le date tra startDate e endDate
        const dates = getDatesBetween(startDate, endDate);
        
        if (editMode) {
            // Aggiorna la pianificazione esistente
            const updatedPlannings = plannings.map(planning => 
                planning.id === editPlanningId ? 
                {
                    ...planning,
                    jobId: selectedJob.value,
                    jobLabel: selectedJob.label,
                    jobType: selectedJob.data?.jobType || 'Altro',
                    jobTypeLabel: selectedJob.data?.jobTypeLabel || 'N/D',
                    jobTypeAssistance: selectedJob.data?.jobTypeAssistance || false,
                    
                    // Informazioni cliente
                    customerId: selectedJob.data?.customerId || null,
                    customerLabel: selectedJob.data?.customerLabel || null,
                    
                    // Informazioni specifiche per assistenza
                    assistanceRequestId: selectedJob.data?.assistanceRequestId || null,
                    assistanceRequestCode: selectedJob.data?.assistanceRequestCode || null,
                    
                    // Informazioni specifiche per manutenzione
                    maintenanceId: selectedJob.data?.maintenanceId || null,
                    maintenanceCode: selectedJob.data?.maintenanceCode || null,
                    maintenanceActions: selectedJob.data?.maintenanceActions || [],
                    
                    startDate,
                    endDate,
                    startTime,
                    endTime,
                    employees: selectedEmployees,
                    vehicles: selectedVehicles,
                    equipments: selectedEquipments,
                    notes: planningNotes,
                    dates
                } : planning
            );
            
            setPlannings(updatedPlannings);
            // Rimosso toast di successo - verrà mostrato solo dopo la creazione sul server
        } else {
            // Crea una nuova pianificazione
            // Genera un ID di gruppo univoco per identificare tutte le pianificazioni che appartengono allo stesso gruppo
            const groupId = generateUniqueId();
            
            const newPlanning = {
                id: generateUniqueId(),
                jobId: selectedJob.value,
                jobLabel: selectedJob.label,
                jobType: selectedJob.data?.jobType || 'Altro', // Aggiungiamo il tipo di commessa
                jobTypeLabel: selectedJob.data?.jobTypeLabel || 'N/D', // Aggiungiamo l'etichetta del tipo di commessa
                jobTypeAssistance: selectedJob.data?.jobTypeAssistance || false, // Indica se è una commessa di tipo assistenza
                
                // Informazioni cliente
                customerId: selectedJob.data?.customerId || null,
                customerLabel: selectedJob.data?.customerLabel || null,
                
                // Informazioni specifiche per assistenza
                assistanceRequestId: selectedJob.data?.assistanceRequestId || null,
                assistanceRequestCode: selectedJob.data?.assistanceRequestCode || null,
                
                // Informazioni specifiche per manutenzione
                maintenanceId: selectedJob.data?.maintenanceId || null,
                maintenanceCode: selectedJob.data?.maintenanceCode || null,
                maintenanceActions: selectedJob.data?.maintenanceActions || [],
                
                startDate,
                endDate,
                startTime,
                endTime,
                employees: selectedEmployees,
                vehicles: selectedVehicles,
                equipments: selectedEquipments,
                notes: planningNotes,
                dates,
                // Aggiungi l'ID di gruppo solo se ci sono più date
                ...(dates.length > 1 ? { groupId: groupId } : {})
            };
            
            setPlannings([...plannings, newPlanning]);
            // Rimosso toast di successo - verrà mostrato solo dopo la creazione sul server
        }
        
        // Reset del form
        resetForm();
    };

    // Funzione per resettare il form
    const resetForm = () => {
        setCurrentStep(1);
        setSelectedJob(null);
        setStartDate(formatDateToISO(new Date()));
        setEndDate(formatDateToISO(new Date()));
        setStartTime('08:00');
        setEndTime('17:00');
        setSelectedEmployees([]);
        setSelectedVehicles([]);
        setSelectedEquipments([]);
        setPlanningNotes('');
        setEditMode(false);
        setEditPlanningId(null);
    };

    // Funzione per gestire la modifica di una pianificazione
    const handleEditPlanning = (planningId) => {
        const planningToEdit = plannings.find(planning => planning.id === planningId);
        
        if (planningToEdit) {
            setEditMode(true);
            setEditPlanningId(planningId);
            setSelectedJob({ value: planningToEdit.jobId, label: planningToEdit.jobLabel });
            setStartDate(planningToEdit.startDate);
            setEndDate(planningToEdit.endDate);
            setStartTime(planningToEdit.startTime);
            setEndTime(planningToEdit.endTime);
            setSelectedEmployees(planningToEdit.employees);
            setSelectedVehicles(planningToEdit.vehicles);
            setSelectedEquipments(planningToEdit.equipments);
            setPlanningNotes(planningToEdit.notes);
            setCurrentStep(4);
        }
    };

    // Funzione per gestire il click sul pulsante elimina
    const handleDeleteClick = (planningId) => {
        // Cerca la pianificazione da eliminare per mostrare i dettagli nella modale
        const planning = plannings.find(p => p.id === planningId);
        setPlanningToDelete(planning);
        setShowDeleteModal(true);
    };
    
    // Funzione per eliminare effettivamente una pianificazione
    const confirmDeletePlanning = () => {
        if (planningToDelete) {
            const updatedPlannings = plannings.filter(planning => planning.id !== planningToDelete.id);
            setPlannings(updatedPlannings);
            toast.success('Pianificazione eliminata con successo!');
            
            // Chiudi la modale e resetta lo stato
            setShowDeleteModal(false);
            setPlanningToDelete(null);
        }
    };
    
    // Funzione per chiudere la modale senza eliminare
    const cancelDelete = () => {
        setShowDeleteModal(false);
        setPlanningToDelete(null);
    };

    return (
        <div className="container-fluid pt-3">
            {/* Modale Calendario in sovraimpressione */}
            {showCalendarModal && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        overflow: 'auto'
                    }}
                    onClick={() => setShowCalendarModal(false)}
                >
                    <div 
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            width: '95%',
                            maxWidth: '1600px',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                            position: 'relative'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Bottone chiusura */}
                        <button
                            onClick={() => setShowCalendarModal(false)}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                zIndex: 10000,
                                backgroundColor: colors.primary || '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '20px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = colors.primaryDark || '#0056b3';
                                e.currentTarget.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = colors.primary || '#007bff';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        
                        {/* Contenuto: JobsCalendar */}
                        <div style={{ padding: '20px' }}>
                            <JobsCalendar />
                        </div>
                    </div>
                </div>
            )}
            
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <button 
                            className="btn btn-outline-secondary mb-2" 
                            onClick={() => navigate('/jobsCalendar')}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> 
                            Torna al calendario
                        </button>
                        
                        <button 
                            className="btn btn-primary mb-2" 
                            onClick={() => setShowCalendarModal(true)}
                            style={{
                                backgroundColor: colors.primary || '#007bff',
                                borderColor: colors.primary || '#007bff'
                            }}
                        >
                            <FontAwesomeIcon icon={faCalendarDays} className="me-2" /> 
                            Visualizza Calendario
                        </button>
                    </div>
                    <h2 className="mt-3">Pianifica Commesse</h2>
                </div>
            </div>
            
            <div className="row">
                {/* Header del wizard a tutta larghezza */}
                <div className="col-12 mb-4">
                    <WizardHeader 
                        steps={steps} 
                        currentStep={currentStep} 
                        setCurrentStep={setCurrentStep}
                        showConnectingLines={false}
                    />
                </div>
                
                {/* Contenuti del wizard e anteprima affiancati */}
                <div className="col-md-8">
                    <div className="card">
                        <div className="card-body p-4">
                            {/* Step 1: Scelta commessa */}
                            {currentStep === 1 && (
                                <div>
                                    <h4 className="mb-3">Scegli la commessa</h4>
                                    <div className="mb-3">
                                        <label className="form-label">Commessa</label>
                                        <JobSelect 
                                            selectedOption={selectedJob}
                                            setSelectedOption={setSelectedJob}
                                            placeholder="Seleziona una commessa..."
                                        />
                                    </div>
                                    
                                    {/* Mini Recap Commessa */}
                                    {selectedJob && selectedJob.data && (
                                        <div className="job-recap mt-2 p-3 border rounded shadow-sm" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #0d6efd', maxWidth: '100%', overflowX: 'hidden', wordWrap: 'break-word' }}>
                                            {/* Riga 1: Codice + Stato + Cliente */}
                                            <div className="d-flex align-items-center mb-2 flex-wrap">
                                                {/* Codice commessa */}
                                                <div className="flex-shrink-0">
                                                    <span className="badge rounded-pill bg-primary me-2" style={{ fontSize: '0.85rem', padding: '0.35em 0.65em' }}>
                                                        {selectedJob.data.code || 'N/D'}
                                                    </span>
                                                </div>
                                                {/* Stato commessa */}
                                                {selectedJob.data.statusLabel && (
                                                    <div className="flex-shrink-0 me-2">
                                                        <span className="badge" style={{ 
                                                            fontSize: '0.8rem', 
                                                            padding: '0.35em 0.65em',
                                                            backgroundColor: getJobStatusColor(selectedJob.data.statusId) 
                                                        }}>
                                                            {selectedJob.data.statusLabel}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Nome cliente */}
                                                <div className="text-truncate flex-grow-1 overflow-hidden">
                                                    <span className="fw-bold fs-6" style={{ color: '#212529' }}>
                                                        {selectedJob.data.customerLabel || 'Cliente N/D'}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            {/* Solo Tipo */}
                                            <div className="d-flex align-items-center mb-2 flex-wrap">
                                                {selectedJob.data.jobTypeLabel && (
                                                    <div className="flex-shrink-0 me-1 mb-1">
                                                        <span className="badge me-1" style={{ 
                                                            fontSize: '0.8rem', 
                                                            maxWidth: '100%', 
                                                            display: 'inline-block', 
                                                            textOverflow: 'ellipsis', 
                                                            overflow: 'hidden',
                                                            backgroundColor: getJobTypeColor(selectedJob.data.jobTypeId)
                                                        }}>
                                                            <i className="fas fa-tools me-1"></i>
                                                            {selectedJob.data.jobTypeLabel}
                                                        </span>
                                                    </div>
                                                )}
                                                
                                                {/* Codice richiesta di assistenza */}
                                                {selectedJob.data.jobTypeAssistance && selectedJob.data.assistanceRequestCode && (
                                                    <div className="flex-shrink-0 mb-1 ms-1">
                                                        <span className="badge bg-success" style={{ fontSize: '0.8rem', maxWidth: '100%', display: 'inline-block', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                            <i className="fas fa-headset me-1"></i>
                                                            Richiesta: {selectedJob.data.assistanceRequestCode}
                                                        </span>
                                                    </div>
                                                )}
                                                
                                                {/* Codice manutenzione */}
                                                {selectedJob.data.jobTypeMaintenance && selectedJob.data.maintenanceCode && (
                                                    <div className="flex-shrink-0 mb-1 ms-1">
                                                        <span className="badge bg-warning text-dark" style={{ fontSize: '0.8rem', maxWidth: '100%', display: 'inline-block', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                            <i className="fas fa-wrench me-1"></i>
                                                            Manutenzione: {selectedJob.data.maintenanceCode}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Informazioni Impianto */}
                                            {(selectedJob.data.systemLabel || selectedJob.data.systemAddressLabel || (selectedJob.data.systemTypeList && selectedJob.data.systemTypeList.length > 0)) && (
                                                <div className="mt-2">
                                                    {selectedJob.data.systemLabel && (
                                                        <div className="mb-1">
                                                            <div className="d-flex align-items-center mb-1">
                                                                <i className="fas fa-building me-2 text-secondary"></i>
                                                                <span className="text-muted small">Impianto:</span>
                                                            </div>
                                                            <div className="ms-4">
                                                                <span className="small fw-bold">{selectedJob.data.systemLabel}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {selectedJob.data.systemAddressLabel && (
                                                        <div className="mb-1">
                                                            <div className="d-flex align-items-center mb-1">
                                                                <i className="fas fa-map-marker-alt me-2 text-secondary"></i>
                                                                <span className="text-muted small">Indirizzo:</span>
                                                            </div>
                                                            <div className="ms-4">
                                                                <span className="small fw-bold">{selectedJob.data.systemAddressLabel}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {selectedJob.data.systemTypeList && selectedJob.data.systemTypeList.length > 0 && (
                                                        <div className="mb-1 d-flex align-items-start">
                                                            <i className="fas fa-cogs me-2 text-secondary mt-1"></i>
                                                            <div>
                                                                <span className="text-muted small">Tipi Impianto:</span>
                                                                <ul className="mb-0 ps-3 mt-1">
                                                                    {selectedJob.data.systemTypeList.map((type, index) => (
                                                                        <li key={index} className="small mb-1">
                                                                            <span className="fw-bold">{type.typeLabel}</span>
                                                                            {type.expirationWarrantyDate && (
                                                                                <div>
                                                                                    <span className="text-muted">Scadenza Garanzia: </span>
                                                                                    <span>{new Date(type.expirationWarrantyDate).toLocaleDateString('it-IT')}</span>
                                                                                </div>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="mt-4 text-end">
                                        <button 
                                            onClick={goToNextStep}
                                            style={{
                                                backgroundColor: colors.primary || '#3a5a40',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '0.25rem',
                                                padding: '0.375rem 0.75rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '400',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Avanti <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Step 2: Schedulazione */}
                            {currentStep === 2 && (
                                <div>
                                    <h4 className="mb-3">Imposta la schedulazione</h4>
                                    
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Data inizio</label>
                                            <input 
                                                type="date" 
                                                className="form-control" 
                                                value={startDate}
                                                onChange={(e) => {
                                                    const newStartDate = e.target.value;
                                                    setStartDate(newStartDate);
                                                    
                                                    // Controllo che la data di inizio non sia successiva a quella di fine
                                                    if (newStartDate > endDate) {
                                                        setEndDate(newStartDate); // Imposta la data di fine uguale a quella di inizio
                                                        toast.info('La data di fine è stata aggiornata per corrispondere alla data di inizio');
                                                    }
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '0.25rem',
                                                    padding: '0.375rem 0.75rem',
                                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                                                }}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Data fine</label>
                                            <input 
                                                type="date" 
                                                className="form-control" 
                                                value={endDate}
                                                min={startDate} // Imposta il valore minimo al valore di startDate
                                                onChange={(e) => {
                                                    const newEndDate = e.target.value;
                                                    
                                                    // Controllo che la data di fine non sia precedente a quella di inizio
                                                    if (newEndDate < startDate) {
                                                        setEndDate(startDate);
                                                        toast.info('La data di fine non può essere precedente alla data di inizio');
                                                    } else {
                                                        setEndDate(newEndDate);
                                                    }
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '0.25rem',
                                                    padding: '0.375rem 0.75rem',
                                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Orario inizio</label>
                                            <input 
                                                type="time" 
                                                className="form-control"
                                                id="startTime" 
                                                value={startTime}
                                                onChange={(e) => {
                                                    const newStartTime = e.target.value;
                                                    setStartTime(newStartTime);
                                                    
                                                    // Controllo che l'orario di inizio non sia successivo all'orario di fine
                                                    if (newStartTime > endTime && startDate === endDate) {
                                                        setEndTime(newStartTime); // Aggiorna l'orario di fine
                                                        toast.info('L\'orario di fine è stato aggiornato per corrispondere all\'orario di inizio');
                                                    }
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '0.25rem',
                                                    padding: '0.375rem 0.75rem',
                                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                                                }}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Orario fine</label>
                                            <input 
                                                type="time" 
                                                className="form-control"
                                                id="endTime" 
                                                value={endTime}
                                                onChange={(e) => {
                                                    const newEndTime = e.target.value;
                                                    
                                                    // Controllo che l'orario di fine non sia precedente all'orario di inizio
                                                    if (newEndTime < startTime && startDate === endDate) {
                                                        setEndTime(startTime);
                                                        toast.info('L\'orario di fine non può essere precedente all\'orario di inizio');
                                                    } else {
                                                        setEndTime(newEndTime);
                                                    }
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '0.25rem',
                                                    padding: '0.375rem 0.75rem',
                                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="mt-4 d-flex justify-content-between">
                                        <button 
                                            onClick={goToPreviousStep}
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: colors.buttonSecondary || '#6c757d',
                                                border: `1px solid ${colors.buttonSecondary || '#6c757d'}`,
                                                borderRadius: '0.25rem',
                                                padding: '0.375rem 0.75rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '400',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} className="me-2" /> Indietro
                                        </button>
                                        <button 
                                            onClick={goToNextStep}
                                            style={{
                                                backgroundColor: colors.primary || '#3a5a40',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '0.25rem',
                                                padding: '0.375rem 0.75rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '400',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Avanti <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Step 3: Assegnazione risorse */}
                            {currentStep === 3 && (
                                <div>
                                    <h4 className="mb-3">Assegna le risorse</h4>
                                    
                                    <div className="mb-4">
                                        <label className="form-label d-flex align-items-center">
                                            <FontAwesomeIcon icon={faUserGroup} className="me-2" />
                                            <span>Dipendenti</span>
                                        </label>
                                        <div className="form-control-wrapper">
                                            <EmployeeSelect 
                                                selectedOptions={selectedEmployees}
                                                setSelectedOptions={setSelectedEmployees}
                                                isMulti={true}
                                                placeholder="Cerca e seleziona dipendenti..."
                                            />
                                            <div className="form-text mt-1">
                                                <small>Puoi selezionare più dipendenti per questa pianificazione</small>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-4">
                                        <label className="form-label d-flex align-items-center">
                                            <FontAwesomeIcon icon={faCar} className="me-2" />
                                            <span>Veicoli</span>
                                        </label>
                                        <div className="form-control-wrapper">
                                            <VehicleSelect 
                                                selectedOptions={selectedVehicles}
                                                setSelectedOptions={setSelectedVehicles}
                                                isMulti={true}
                                                placeholder="Cerca e seleziona veicoli..."
                                            />
                                            <div className="form-text mt-1">
                                                <small>Puoi selezionare più veicoli per questa pianificazione</small>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-4">
                                        <label className="form-label d-flex align-items-center">
                                            <FontAwesomeIcon icon={faToolbox} className="me-2" />
                                            <span>Attrezzature</span>
                                        </label>
                                        <div className="form-control-wrapper">
                                            <EquipmentSelect 
                                                selectedOptions={selectedEquipments}
                                                setSelectedOptions={setSelectedEquipments}
                                                isMulti={true}
                                                placeholder="Cerca e seleziona attrezzature..."
                                            />
                                            <div className="form-text mt-1">
                                                <small>Puoi selezionare più attrezzature per questa pianificazione</small>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-4 d-flex justify-content-between">
                                        <button 
                                            onClick={goToPreviousStep}
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: colors.buttonSecondary || '#6c757d',
                                                border: `1px solid ${colors.buttonSecondary || '#6c757d'}`,
                                                borderRadius: '0.25rem',
                                                padding: '0.375rem 0.75rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '400',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} className="me-2" /> Indietro
                                        </button>
                                        <button 
                                            onClick={goToNextStep}
                                            style={{
                                                backgroundColor: colors.primary || '#3a5a40',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '0.25rem',
                                                padding: '0.375rem 0.75rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '400',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Avanti <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Step 4: Note */}
                            {currentStep === 4 && (
                                <div>
                                    <h4 className="mb-3">Aggiungi note</h4>
                                    
                                    <div className="mb-3">
                                        <label className="form-label d-flex align-items-center mb-2">
                                            <FontAwesomeIcon icon={faNoteSticky} className="me-2" />
                                            <span>Note sulla pianificazione</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="5"
                                            placeholder="Inserisci note sulla pianificazione..."
                                            value={planningNotes}
                                            onChange={(e) => setPlanningNotes(e.target.value)}
                                        ></textarea>
                                    </div>
                                    
                                    <div className="mt-4 d-flex justify-content-between">
                                        <button 
                                            onClick={goToPreviousStep}
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: colors.buttonSecondary || '#6c757d',
                                                border: `1px solid ${colors.buttonSecondary || '#6c757d'}`,
                                                borderRadius: '0.25rem',
                                                padding: '0.375rem 0.75rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '400',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} className="me-2" /> Indietro
                                        </button>
                                        <button 
                                            className={`btn ${editMode ? 'btn-warning' : 'btn-primary'}`}
                                            onClick={addOrUpdatePlanning}
                                            disabled={!selectedJob}
                                            style={!editMode ? {
                                                backgroundColor: '#383838',
                                                borderColor: '#383838',
                                                color: 'white'
                                            } : {}}
                                            onMouseEnter={!editMode ? (e) => {
                                                e.currentTarget.style.backgroundColor = '#2a2a2a';
                                                e.currentTarget.style.borderColor = '#2a2a2a';
                                            } : undefined}
                                            onMouseLeave={!editMode ? (e) => {
                                                e.currentTarget.style.backgroundColor = '#383838';
                                                e.currentTarget.style.borderColor = '#383838';
                                            } : undefined}
                                        >
                                            {editMode ? (
                                                <>
                                                    <FontAwesomeIcon icon={faPencilAlt} className="me-2" />
                                                    Aggiorna Pianificazione
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                    Aggiungi Pianificazione
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Colonna di destra: Anteprima */}
                <div className="col-md-4">
                    <div className="card">
                        <div className="card-header bg-light">
                            <h5 className="mb-0">Anteprima Pianificazione</h5>
                        </div>
                        <div className="card-body">
                            {selectedJob ? (
                                <div>
                                    <div className="mb-3">
                                        <h6>Commessa</h6>
                                        <div className="d-flex flex-wrap align-items-center">
                                            {/* Codice commessa */}
                                            {selectedJob.data?.code && (
                                                <span className="badge me-2 mb-1" style={{ fontSize: '0.9rem', backgroundColor: '#383838', color: 'white' }}>
                                                    {selectedJob.data.code}
                                                </span>
                                            )}
                                            
                                            {/* Stato commessa */}
                                            {selectedJob.data?.statusLabel && (
                                                <span className="badge me-2 mb-1" style={{ 
                                                    fontSize: '0.8rem',
                                                    backgroundColor: getJobStatusColor(selectedJob.data.statusId)
                                                }}>
                                                    {selectedJob.data.statusLabel}
                                                </span>
                                            )}
                                            
                                            {/* Nome cliente */}
                                            {selectedJob.data?.customerLabel && (
                                                <span className="mb-1" style={{ fontSize: '0.9rem', fontWeight: "500" }}>
                                                    {selectedJob.data.customerLabel}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <h6>Tipo Commessa</h6>
                                        {selectedJob.data?.jobTypeLabel && (
                                            <div className="mb-2">
                                                <span className="badge" style={{ 
                                                    fontSize: '0.85rem',
                                                    backgroundColor: getJobTypeColor(selectedJob.data.jobTypeId)
                                                }}>
                                                    {selectedJob.data.jobTypeLabel}
                                                </span>
                                            </div>
                                        )}
                                        {selectedJob.data?.jobTypeAssistance ? (
                                            <div>
                                                {selectedJob.data?.assistanceRequestCode && (
                                                    <div className="mt-1">
                                                        <small className="text-muted">Codice Assistenza:</small>
                                                        <div className="fw-bold">{selectedJob.data.assistanceRequestCode}</div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : selectedJob.data?.jobType === 'maintenance' ? (
                                            <div>
                                                {selectedJob.data?.maintenanceCode && (
                                                    <div className="mt-1">
                                                        <small className="text-muted">Codice Manutenzione:</small>
                                                        <div className="fw-bold">{selectedJob.data.maintenanceCode}</div>
                                                    </div>
                                                )}
                                                {selectedJob.data?.maintenanceActions?.length > 0 && (
                                                    <div className="mt-2">
                                                        <small className="text-muted">Azioni Manutenzione:</small>
                                                        <ul className="ps-3 mt-1 mb-0">
                                                            {selectedJob.data.maintenanceActions.map((action, index) => (
                                                                <li key={index} className="small">{action.name || action.label || 'Azione non specificata'}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="badge bg-info">{selectedJob.data?.jobTypeLabel || 'N/D'}</span>
                                        )}
                                    </div>
                                    
                                    <div className="mb-3">
                                        <h6>Periodo</h6>
                                        {startDate && endDate ? (
                                            <p className="mb-1">
                                                {startDate === endDate ? (
                                                    <span>Il {new Date(startDate).toLocaleDateString('it-IT')}</span>
                                                ) : (
                                                    <span>Dal {new Date(startDate).toLocaleDateString('it-IT')} al {new Date(endDate).toLocaleDateString('it-IT')}</span>
                                                )}
                                                <br />
                                                Dalle {startTime} alle {endTime}
                                            </p>
                                        ) : (
                                            <p className="text-muted">Nessuna data selezionata</p>
                                        )}
                                    </div>
                                    
                                    <div className="mb-3">
                                        <h6>Risorse assegnate</h6>
                                        <div>
                                            <strong>Dipendenti:</strong> 
                                            {selectedEmployees.length > 0 ? (
                                                <ul className="mb-2 ps-3">
                                                    {selectedEmployees.map(emp => (
                                                        <li key={emp.value}>{emp.label}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted mb-2">Nessun dipendente selezionato</p>
                                            )}
                                        </div>
                                        
                                        <div>
                                            <strong>Veicoli:</strong> 
                                            {selectedVehicles.length > 0 ? (
                                                <ul className="mb-2 ps-3">
                                                    {selectedVehicles.map(veh => (
                                                        <li key={veh.value}>{veh.label}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted mb-2">Nessun veicolo selezionato</p>
                                            )}
                                        </div>
                                        
                                        <div>
                                            <strong>Attrezzature:</strong> 
                                            {selectedEquipments.length > 0 ? (
                                                <ul className="mb-2 ps-3">
                                                    {selectedEquipments.map(eq => (
                                                        <li key={eq.value}>{eq.data?.name || eq.data?.title || eq.label || 'Attrezzatura non specificata'}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted mb-2">Nessuna attrezzatura selezionata</p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {planningNotes && (
                                        <div className="mb-3">
                                            <h6>Note</h6>
                                            <p className="mb-1">{planningNotes}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className="text-muted">Seleziona una commessa per visualizzare l'anteprima</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Lista delle pianificazioni create - spostata sotto il wizard */}
            {plannings.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-end mb-3">
                            <button 
                                className="btn btn-success" 
                                onClick={() => setShowCreateModal(true)}
                                style={{
                                    backgroundColor: '#383838',
                                    borderColor: '#383838',
                                    color: 'white'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#2a2a2a';
                                    e.currentTarget.style.borderColor = '#2a2a2a';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#383838';
                                    e.currentTarget.style.borderColor = '#383838';
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Crea pianificazioni
                            </button>
                        </div>
                        <div className="card">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Pianificazioni da creare</h5>
                                <span className="badge" style={{ backgroundColor: '#383838', color: 'white' }}>{plannings.length} pianificazioni</span>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Commessa</th>
                                                <th>Tipo Commessa</th>
                                                <th>Periodo</th>
                                                <th>Orario</th>
                                                <th>Risorse</th>
                                                <th>Azioni</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {plannings.map((planning) => (
                                                <tr key={planning.id}>
                                                    <td>
                                                        <div className="fw-bold">{planning.jobLabel}</div>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {planning.jobTypeAssistance ? (
                                                                <div>
                                                                    <span className="badge bg-warning text-dark mb-1">Assistenza</span>
                                                                    {planning.assistanceRequestCode && (
                                                                        <div className="mt-1 small">
                                                                            <span className="text-muted">Cod: </span> 
                                                                            <span className="fw-bold">{planning.assistanceRequestCode}</span>
                                                                        </div>
                                                                    )}
                                                                    {/* Badge stato commessa */}
                                                                    {planning.statusLabel && (
                                                                        <div className="mt-1">
                                                                            <span className="badge" style={{ 
                                                                                fontSize: '0.75rem',
                                                                                backgroundColor: getJobStatusColor(planning.statusId)
                                                                            }}>
                                                                                {planning.statusLabel}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {/* Badge stato assistenza */}
                                                                    {planning.assistanceRequestStatusLabel && planning.assistanceRequestStatusId && (
                                                                        <div className="mt-1">
                                                                            <span className="badge" style={{ 
                                                                                fontSize: '0.75rem',
                                                                                backgroundColor: getAssistanceStatusColor(planning.assistanceRequestStatusId)
                                                                            }}>
                                                                                Assistenza: {planning.assistanceRequestStatusLabel}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : planning.jobType === 'maintenance' ? (
                                                                <div>
                                                                    <span className="badge bg-info mb-1">Manutenzione</span>
                                                                    {planning.maintenanceCode && (
                                                                        <div className="mt-1 small">
                                                                            <span className="text-muted">Cod: </span> 
                                                                            <span className="fw-bold">{planning.maintenanceCode}</span>
                                                                        </div>
                                                                    )}
                                                                    {/* Badge stato commessa */}
                                                                    {planning.statusLabel && (
                                                                        <div className="mt-1">
                                                                            <span className="badge" style={{ 
                                                                                fontSize: '0.75rem',
                                                                                backgroundColor: getJobStatusColor(planning.statusId)
                                                                            }}>
                                                                                {planning.statusLabel}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {/* Badge stato manutenzione */}
                                                                    {planning.maintenanceStatusLabel && planning.maintenanceStatusId && (
                                                                        <div className="mt-1">
                                                                            <span className="badge" style={{ 
                                                                                fontSize: '0.75rem',
                                                                                backgroundColor: getMaintenanceStatusColor(planning.maintenanceStatusId)
                                                                            }}>
                                                                                Manutenzione: {planning.maintenanceStatusLabel}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {planning.maintenanceActions?.length > 0 && (
                                                                        <div className="mt-1">
                                                                            <span className="small text-muted">Azioni:</span>
                                                                            <ul className="ps-3 mt-1 mb-0">
                                                                                {planning.maintenanceActions.slice(0, 2).map((action, index) => (
                                                                                    <li key={index} className="small">{action.name || action.label || 'Azione non specificata'}</li>
                                                                                ))}
                                                                                {planning.maintenanceActions.length > 2 && (
                                                                                    <li className="small text-muted">+ {planning.maintenanceActions.length - 2} altre...</li>
                                                                                )}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <span className="badge bg-info">{planning.jobTypeLabel || 'N/D'}</span>
                                                                    {/* Badge stato commessa */}
                                                                    {planning.statusLabel && (
                                                                        <div className="mt-1">
                                                                            <span className="badge" style={{ 
                                                                                fontSize: '0.75rem',
                                                                                backgroundColor: getJobStatusColor(planning.statusId)
                                                                            }}>
                                                                                {planning.statusLabel}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {planning.startDate === planning.endDate ? (
                                                            <span>Il {new Date(planning.startDate).toLocaleDateString('it-IT')}</span>
                                                        ) : (
                                                            <span>Dal {new Date(planning.startDate).toLocaleDateString('it-IT')} <br />al {new Date(planning.endDate).toLocaleDateString('it-IT')}</span>
                                                        )}
                                                    </td>
                                                    <td>Dalle {planning.startTime} alle {planning.endTime}</td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            {planning.employees.length > 0 && (
                                                                <div className="mb-2">
                                                                    <span className="badge me-1" style={{ backgroundColor: '#383838', color: 'white' }}>
                                                                        <FontAwesomeIcon icon={faUserGroup} className="me-1" />
                                                                        Dipendenti
                                                                    </span>
                                                                    <div className="mt-1 ms-2">
                                                                        <ul className="list-unstyled mb-0">
                                                                            {planning.employees.map(emp => (
                                                                                <li key={emp.value} className="small">
                                                                                    <i className="fas fa-user-circle me-1 text-primary"></i> {emp.label}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {planning.vehicles.length > 0 && (
                                                                <div className="mb-2">
                                                                    <span className="badge bg-success me-1">
                                                                        <FontAwesomeIcon icon={faCar} className="me-1" />
                                                                        Veicoli
                                                                    </span>
                                                                    <div className="mt-1 ms-2">
                                                                        <ul className="list-unstyled mb-0">
                                                                            {planning.vehicles.map(veh => (
                                                                                <li key={veh.value} className="small">
                                                                                    <i className="fas fa-car-side me-1 text-success"></i> {veh.label}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {planning.equipments.length > 0 && (
                                                                <div className="mb-0">
                                                                    <span className="badge bg-warning text-dark me-1">
                                                                        <FontAwesomeIcon icon={faToolbox} className="me-1" />
                                                                        Attrezzature
                                                                    </span>
                                                                    <div className="mt-1 ms-2">
                                                                        <ul className="list-unstyled mb-0">
                                                                            {planning.equipments.map(eq => (
                                                                                <li key={eq.value} className="small">
                                                                                    <i className="fas fa-tools me-1 text-warning"></i> {eq.data?.name || eq.data?.title || eq.label || 'Attrezzatura non specificata'}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {planning.employees.length === 0 && planning.vehicles.length === 0 && planning.equipments.length === 0 && (
                                                                <span className="text-muted">Nessuna risorsa assegnata</span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="d-flex">
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary me-2" 
                                                                title="Modifica pianificazione"
                                                                onClick={() => handleEditPlanning(planning.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faPencilAlt} />
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-outline-danger" 
                                                                title="Elimina pianificazione"
                                                                onClick={() => handleDeleteClick(planning.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrashAlt} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Modale di conferma eliminazione */}
            {showDeleteModal && planningToDelete && (
                <div className="modal d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">Conferma eliminazione</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>Sei sicuro di voler eliminare questa pianificazione?</p>
                                
                                <div className="bg-light p-3 rounded mb-3">
                                    <h6 className="mb-2">{planningToDelete.jobLabel}</h6>
                                    <p className="mb-1 small">
                                        {planningToDelete.startDate === planningToDelete.endDate ? (
                                            <span>Il {new Date(planningToDelete.startDate).toLocaleDateString('it-IT')}</span>
                                        ) : (
                                            <span>Dal {new Date(planningToDelete.startDate).toLocaleDateString('it-IT')} al {new Date(planningToDelete.endDate).toLocaleDateString('it-IT')}</span>
                                        )}
                                        <br />
                                        Dalle {planningToDelete.startTime} alle {planningToDelete.endTime}
                                    </p>
                                </div>
                                
                                <p className="text-danger">Questa operazione non può essere annullata.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>
                                    Annulla
                                </button>
                                <button type="button" className="btn btn-danger" onClick={confirmDeletePlanning}>
                                    Elimina
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modale di conferma creazione pianificazioni */}
            {showCreateModal && (
                <div className="modal d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-success text-white">
                                <h5 className="modal-title">Conferma creazione pianificazioni</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCreateModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p>Stai per creare {plannings.length} pianificazioni. Vuoi procedere?</p>
                                
                                <div className="bg-light p-3 rounded mb-3">
                                    <p className="mb-1"><strong>{plannings.length}</strong> pianificazioni saranno create nel sistema.</p>
                                    <p>Dopo la creazione, saranno visibili nel calendario delle pianificazioni.</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Annulla
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-success" 
                                    onClick={async () => {
                                        let loadingToast = null;
                                        try {
                                            console.log('🚀 [DEBUG] Iniziando creazione pianificazioni...');
                                            console.log('📋 [DEBUG] Numero pianificazioni da creare:', plannings.length);
                                            console.log('📊 [DEBUG] Dati pianificazioni:', plannings);
                                            
                                            // Mostra un toast di caricamento
                                            console.log('⏳ [DEBUG] Mostrando toast di caricamento');
                                            loadingToast = toast.loading('Creazione pianificazioni in corso...');
                                            
                                            // Ottieni accountId dall'utente loggato
                                            const accountId = accountData?._id;
                                            console.log('🔑 [DEBUG] AccountId:', accountId);
                                            
                                            if (!accountId) {
                                                console.log('❌ [DEBUG] AccountId non disponibile');
                                                toast.error('ID account non disponibile');
                                                toast.dismiss(loadingToast);
                                                return;
                                            }
                                            
                                            // Aggiungi il campo timezoneDiff a ciascuna pianificazione
                                            const timezoneOffset = moment().utcOffset();
                                            
                                            // Prepara le pianificazioni con il campo timezoneDiff
                                            const planningsWithTimezone = plannings.map(planning => ({
                                                ...planning,
                                                timezoneDiff: timezoneOffset, // Aggiungi l'offset del fuso orario
                                                // Assicurati che le date rimangano esattamente come sono state inserite dall'utente
                                                // senza conversioni automatiche a UTC
                                                _rawDates: true // Flag per il server per conservare le date
                                            }));
                                            
                                            console.log('Pianificazioni da inviare al server:', planningsWithTimezone);
                                            
                                            console.log('📡 [DEBUG] Preparando chiamata API...');
                                            console.log('🌐 [DEBUG] URL API:', `${import.meta.env.VITE_API_BASE_URL}/appointments/createPlannings`);
                                            console.log('📦 [DEBUG] Payload da inviare:', { accountId, plannings: planningsWithTimezone });
                                            
                                            // Chiamata API per creare le pianificazioni
                                            const response = await axios.post(
                                                `${import.meta.env.VITE_API_BASE_URL}/appointments/createPlannings`,
                                                {
                                                    accountId,
                                                    plannings: planningsWithTimezone
                                                },
                                                {
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        'auth-token': token
                                                    }
                                                }
                                            );
                                            
                                            console.log('📨 [DEBUG] Risposta API ricevuta - Status:', response.status);
                                            console.log('📄 [DEBUG] Headers risposta:', response.headers);
                                            console.log('💾 [DEBUG] Dati risposta completi:', response.data);
                                            
                                            // Nascondi il toast di caricamento
                                            toast.dismiss(loadingToast);
                                            
                                            // Verifica la risposta
                                            console.log('🔍 [DEBUG] Risposta ricevuta dal server:', response.data);
                                            
                                            if (response.data.success) {
                                                const successCount = response.data.data.success.length;
                                                const errorCount = response.data.data.errors.length;
                                                
                                                console.log('✅ [DEBUG] Pianificazioni create - Successi:', successCount, 'Errori:', errorCount);
                                                
                                                // Messaggio di conferma dettagliato
                                                if (errorCount === 0) {
                                                    console.log('🎉 [DEBUG] Mostrando toast di successo completo');
                                                    toast.success(`✅ Tutte le ${successCount} pianificazioni sono state create con successo!`);
                                                } else {
                                                    console.log('⚠️ [DEBUG] Mostrando toast di successo parziale');
                                                    toast.success(`✅ ${successCount} pianificazioni create con successo`);
                                                    toast.warning(`⚠️ ${errorCount} pianificazioni hanno avuto errori`);
                                                }
                                                
                                                console.log('🔄 [DEBUG] Chiudendo modale e svuotando lista pianificazioni');
                                                // Chiudi la modale e svuota la lista delle pianificazioni
                                                setShowCreateModal(false);
                                                setPlannings([]);
                                                
                                                // Messaggio aggiuntivo di conferma
                                                console.log('⏰ [DEBUG] Programmando toast informativo in 1.5 secondi');
                                                setTimeout(() => {
                                                    console.log('📅 [DEBUG] Mostrando toast informativo calendario');
                                                    toast.info(`📅 Le pianificazioni sono ora visibili nel calendario`);
                                                }, 1500);
                                            } else {
                                                console.log('❌ [DEBUG] Errore nella risposta del server:', response.data);
                                                toast.error('Errore durante la creazione delle pianificazioni');
                                            }
                                        } catch (error) {
                                            console.error('💥 [DEBUG] Errore durante la chiamata API:', error);
                                            console.error('📋 [DEBUG] Dettagli errore:', {
                                                message: error.message,
                                                status: error.response?.status,
                                                statusText: error.response?.statusText,
                                                data: error.response?.data,
                                                stack: error.stack
                                            });
                                            
                                            // Nascondi il toast di caricamento se presente
                                            if (loadingToast) {
                                                toast.dismiss(loadingToast);
                                            }
                                            
                                            console.log('🚨 [DEBUG] Mostrando toast di errore');
                                            toast.error(error.response?.data?.message || 'Errore durante la creazione delle pianificazioni');
                                            setShowCreateModal(false);
                                        }
                                    }}
                                >
                                    Conferma
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* ToastContainer per mostrare le notifiche */}
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default JobScheduling;
